/* eslint-disable */
import { useEffect, useCallback, useState } from "react";
import { useRouter } from "next/router";
import Modal from "react-modal";
import { appWithTranslation } from "next-i18next";
import { useTranslation } from "next-i18next";
import { ToastContainer } from "react-toastify";
import { useIsMounted } from "@hooks/useIsMounted";
import ErrorNotifier from "@components/ErrorNotifier/ErrorNotifier";
import { StoreProvider } from "@hooks/useZustandProvider";
import { MediaQueryProvider } from "@hooks/useMediaQuery";
import { MousePositionProvider } from "@hooks/useMousePosition.js";
import { CookiebotProvider } from "@hooks/useCookiebot.js";
import { useHydrate } from "@store/store";
import { PreferredLanguageProvider } from "@hooks/usePreferredLanguage";
import { ApolloProvider, useApollo } from "@lib/apollo";
import { MainLayout } from "@layouts/MainLayout/MainLayout";
import { UserLayout } from "@layouts/UserLayout/UserLayout";
import { CmsLayout } from "@layouts/CmsLayout/CmsLayout";
import ReactTooltip from "@ui-kit/ReactTooltip";
import nextI18NextConfig from "../next-i18next.config.js";
import "@styles/index.scss";
import { getCookie, setCookie } from "cookies-next";
import pkg from '../package.json'
import { isDev, isStage } from "@config/config.js";
import colors from "@styles/variables.module.scss";
import ServerTimeout from "@modules/ServerTimeout/ServerTimeout";
import ServerError from "@modules/ServerError/ServerError";

// export function reportWebVitals(metric) {
//   console.log("!metric!: ", metric);
// }

Modal.setAppElement("#__next");

function MyApp({ Component, pageProps }) {
  const { pathname, events } = useRouter();
  const isServerError = Boolean(pageProps.isServerError);
  const [hasServerError, setHasServerError] = useState(isServerError);

  // Keep the server fallback mounted until navigation succeeds or the document
  // reloads, even if Next resolves the original route without its SSR data.
  useEffect(() => {
    if (isServerError) setHasServerError(true);
  }, [isServerError]);

  useEffect(() => {
    const onNavigationComplete = () => setHasServerError(false);
    events.on("routeChangeComplete", onNavigationComplete);
    return () => events.off("routeChangeComplete", onNavigationComplete);
  }, [events]);

  // Keep fallback pages independent of API queries, persisted CMS editor state,
  // auth redirects and purchase-flow side effects in the normal layouts.
  if (pageProps.isTimeout) return <ServerTimeout />;
  if (isServerError || hasServerError) {
    return <ServerError statusCode={pageProps.statusCode || 500} />;
  }
  if (Component.isErrorPage || pathname === "/_error") {
    return <Component {...pageProps} />;
  }
  return <Application Component={Component} pageProps={pageProps} />;
}

function Application({ Component, pageProps }) {
  const router = useRouter();
  const isMounted = useIsMounted();
  const { locale } = router;
  const { i18n } = useTranslation();
  const apolloClient = useApollo(pageProps);

  const store = useHydrate(pageProps.initialZustandState);

  const onRouteChangeComplete = useCallback(
    () => {
      const cookieName = 'cversion';
      const currentCversion = getCookie(cookieName);

      setCookie(cookieName, currentCversion, {
        maxAge: 60 * 60, // 1 hour
        path: '/',
      });

      document.body.scrollTo({ top: 0, behavior: "smooth" })
    },
    []
  );

  useEffect(() => {
    if (isMounted) {
      i18n.changeLanguage(locale);
    }

    if (window.dataLayer) {
      dataLayer.push({
        event: "languageChanged",
        language: locale,
      });
    }
  }, [locale]);

  useEffect(() => {
    if (pageProps.initialZustandState) {
      const state = store.getState()
      const { updateSsrData } = state;
      updateSsrData(pageProps.initialZustandState);
    }
  }, [pageProps.initialZustandState])

  useEffect(() => {

    if (isDev || isStage) {
      console.log(pkg.version);
    }

    window.info = {
      version: pkg.version
    }

    router.events.on("routeChangeComplete", onRouteChangeComplete);

    return () => {
      router.events.off("routeChangeComplete", onRouteChangeComplete);
    };
  }, []);

  // useEffect(() => {
  //   router.events.on("routeChangeStart", (url, { shallow }) => {
  //     console.log(`App is changing to ${url}`);
  //   });
  //   router.events.on("routeChangeComplete", (url, { shallow }) => {
  //     console.log(`App is Changed to ${url}`);
  //   });
  // }, [router.query.counter]);

  return (
    <StoreProvider store={store}>
      <ErrorNotifier>
        {(showError) => (
          <ApolloProvider showError={showError} client={apolloClient}>
            <PreferredLanguageProvider>
              <CookiebotProvider>
                <MediaQueryProvider>
                  <MousePositionProvider>
                    <UserLayout>
                      <MainLayout>
                        <CmsLayout>
                          <Component {...pageProps} />
                          <ReactTooltip />
                        </CmsLayout>
                      </MainLayout>
                    </UserLayout>
                  </MousePositionProvider>
                </MediaQueryProvider>
              </CookiebotProvider>
            </PreferredLanguageProvider>
          </ApolloProvider>
        )}
      </ErrorNotifier>
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </StoreProvider>
  );
}

export default appWithTranslation(MyApp, nextI18NextConfig);
